import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaEye, FaFilePdf, FaPaperPlane, FaTimes } from "react-icons/fa";
import EmailRichTextEditor, {
  isEmptyEmailHtml,
} from "../GenericComponents/EmailRichTextEditor";
import InvoiceXeroPreview from "./InvoiceXeroPreview";
import { getUser } from "../../utilis/Common";
import { getPdfBlobFromFinanceInvoiceDraft } from "../GeneratePDF/logic";
import {
  buildInvoicePdfFilename,
  previewFinanceInvoiceEmailRecipient,
  sendFinanceInvoiceApproveEmail,
} from "../../utilis/financeInvoiceApproveEmailApi";
import { loadInvoicePreviewDrafts } from "../../utilis/financeInvoiceEmailHelpers";
import { resolveInvoiceEmailBranchLabel } from "../../utilis/invoicePreviewHelpers";
import { showCommissionInvoiceSuccessToast } from "../reports/commissions/commissionInvoiceToast";

const inputClass =
  "h-9 w-full rounded border border-[#eef0ff] bg-[#f8f9ff] px-2.5 text-[13px] font-medium text-slate-800 outline-none transition focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20";

function isValidEmail(value = "") {
  const text = String(value || "").trim();
  if (!text) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
}

function parseEmailTokens(value = "") {
  return String(value || "")
    .split(/[,;\s\n]+/)
    .map((email) => email.trim())
    .filter(Boolean);
}

function escapeHtml(text = "") {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function getBranchPhone(branch = "") {
  const key = String(branch || "")
    .trim()
    .toLowerCase();
  if (key === "lahore") return "0092 344 000 2829";
  if (key === "sydney") return "0061 2 8212 4505 | 0432 710 739";
  if (key === "samundri") return "0092 334 4666 480";
  if (key === "sahiwal") return "0092 300 696 3438";
  if (key === "faislabad") return "0092 305 184 4480";
  return "";
}

function buildEmailSignatureHtml(userName, branchLabel, branchPhone) {
  const safeBranch = String(branchLabel || "").trim();
  const branchLine = safeBranch
    ? `<p>Aptitude Migration (${escapeHtml(safeBranch)} Office)</p>`
    : "<p>Aptitude Migration</p>";
  const phoneLine = branchPhone
    ? `<p>${escapeHtml(branchPhone)}</p>`
    : "<p><br></p>";

  return [
    "<p>Thank you</p>",
    `<p><strong>${escapeHtml(userName || "Team member")}</strong></p>`,
    branchLine,
    phoneLine,
  ].join("");
}

function buildDefaultEmailBody({
  recipientName = "there",
  invoiceNumbers = [],
  userName = "Team member",
  branchLabel = "",
  branchPhone = "",
  isCommission = false,
}) {
  const greeting = `<p>Hi ${escapeHtml(recipientName)},</p>`;
  const numbers = (invoiceNumbers || []).map(String).filter(Boolean);
  const invoiceRef =
    numbers.length > 1
      ? ` invoices <strong>${numbers.map(escapeHtml).join(", ")}</strong>`
      : numbers.length === 1
        ? ` invoice <strong>${escapeHtml(numbers[0])}</strong>`
        : " invoice";
  const intro = isCommission
    ? `<p>Please find attached the commission${invoiceRef}. If you have any questions, please let us know.</p><p><br></p>`
    : `<p>Please find attached${invoiceRef}. If you have any questions, please let us know.</p><p><br></p>`;

  return `${greeting}${intro}${buildEmailSignatureHtml(
    userName,
    branchLabel,
    branchPhone,
  )}`;
}

function buildDefaultEmailSubject({ invoiceNumbers = [], isCommission = false }) {
  const numbers = (invoiceNumbers || []).map(String).filter(Boolean);
  if (!numbers.length) {
    return isCommission
      ? "Commission invoice from Aptitude Migration"
      : "Invoice from Aptitude Migration";
  }
  if (numbers.length === 1) {
    return isCommission
      ? `Commission invoice ${numbers[0]} from Aptitude Migration`
      : `Invoice ${numbers[0]} from Aptitude Migration`;
  }
  return isCommission
    ? `Commission invoices ${numbers.join(", ")} from Aptitude Migration`
    : `Invoices ${numbers.join(", ")} from Aptitude Migration`;
}

function getRecipientHint(preview = {}, isCommission = false) {
  if (!preview?.has_saved_email) {
    return isCommission
      ? "No Accounts Manager email on file. Enter the provider email address to send."
      : "Client email is missing. Enter the recipient email address to send.";
  }

  const designation = String(preview.recipient_designation || "").trim();
  const contactName = String(preview.recipient_contact_name || "").trim();

  if (isCommission && designation.toLowerCase() === "accounts manager") {
    return contactName
      ? `Loaded from Accounts Manager contact (${contactName}).`
      : "Loaded from Accounts Manager contact.";
  }

  if (designation) {
    return contactName
      ? `Loaded from ${designation} contact (${contactName}).`
      : `Loaded from ${designation} contact.`;
  }

  if (isCommission) {
    return contactName
      ? `Loaded from provider contact (${contactName}).`
      : "Loaded from provider contact.";
  }

  return contactName
    ? `Loaded from client profile (${contactName}).`
    : "Loaded from client profile.";
}

function CcEmailsField({ emails = [], onChange, disabled = false }) {
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");

  const addEmails = (rawValue) => {
    const tokens = parseEmailTokens(rawValue);
    if (!tokens.length) return;

    const invalid = tokens.find((email) => !isValidEmail(email));
    if (invalid) {
      setInputError(`"${invalid}" is not a valid email address.`);
      return;
    }

    const existing = new Set(emails.map((email) => email.trim().toLowerCase()));
    const next = [...emails];
    tokens.forEach((email) => {
      const normalized = email.trim().toLowerCase();
      if (!existing.has(normalized)) {
        existing.add(normalized);
        next.push(email.trim());
      }
    });

    onChange(next);
    setInputValue("");
    setInputError("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === "," || event.key === " ") {
      event.preventDefault();
      if (inputValue.trim()) addEmails(inputValue);
      return;
    }
    if (event.key === "Tab" && inputValue.trim()) {
      event.preventDefault();
      addEmails(inputValue);
      return;
    }
    if (event.key === "Backspace" && !inputValue && emails.length) {
      onChange(emails.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) addEmails(inputValue);
  };

  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-semibold text-slate-700">
        CC
      </span>
      <div
        className={`min-h-9 rounded border bg-[#f8f9ff] px-2 py-1.5 transition focus-within:border-[#007BFF] focus:ring-1 focus-within:ring-[#007BFF]/20 ${
          inputError
            ? "border-red-300 focus-within:border-red-500 focus-within:ring-red-500/20"
            : "border-[#eef0ff]"
        }`}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {emails.map((email, index) => (
            <span
              key={`${email}-${index}`}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200"
            >
              <span className="truncate">{email}</span>
              <button
                type="button"
                onClick={() =>
                  onChange(emails.filter((_, itemIndex) => itemIndex !== index))
                }
                disabled={disabled}
                className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                aria-label={`Remove ${email}`}
              >
                <FaTimes size={9} />
              </button>
            </span>
          ))}
          <input
            type="text"
            value={inputValue}
            onChange={(event) => {
              setInputValue(event.target.value);
              if (inputError) setInputError("");
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={emails.length ? "Add another email" : "Enter CC emails"}
            className="min-w-[140px] flex-1 border-0 bg-transparent px-1 py-0.5 text-[13px] font-medium text-slate-800 outline-none"
          />
        </div>
      </div>
      {inputError ? (
        <p className="mb-0 mt-1 text-[11px] font-medium text-red-600">
          {inputError}
        </p>
      ) : (
        <p className="mb-0 mt-1 text-[11px] text-slate-500">
          Optional. Add multiple emails separated by comma, space, or Enter.
        </p>
      )}
    </label>
  );
}

function FinanceInvoiceApproveEmailModal({
  open = false,
  invoiceId = null,
  invoiceIds = null,
  clientId = null,
  isCommission = false,
  fallbackRecipientEmail = "",
  fallbackRecipientName = "",
  fallbackInvoiceNumber = "",
  onClose,
  onSent,
  onApprove,
}) {
  const loggedInUser = useMemo(() => getUser() || {}, []);
  const pdfCaptureRef = useRef(null);
  const captureDraftRef = useRef(null);
  const capturePromiseRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [captureDraft, setCaptureDraft] = useState(null);
  const [pdfPreviewDraft, setPdfPreviewDraft] = useState(null);
  const [subject, setSubject] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [ccEmails, setCcEmails] = useState([]);
  const [emailBody, setEmailBody] = useState("");

  const resolvedInvoiceIds = useMemo(() => {
    const fromProp = Array.isArray(invoiceIds)
      ? invoiceIds.map(String).filter(Boolean)
      : [];
    if (fromProp.length) return fromProp;
    return invoiceId ? [String(invoiceId)] : [];
  }, [invoiceId, invoiceIds]);

  const branchLabel = useMemo(
    () => resolveInvoiceEmailBranchLabel(loggedInUser),
    [loggedInUser],
  );
  const branchPhone = getBranchPhone(branchLabel);

  const invoiceNumbers = useMemo(
    () =>
      attachments
        .map((item) => item.invoiceNumber)
        .filter(Boolean),
    [attachments],
  );

  const primaryInvoiceNumber =
    invoiceNumbers[0] ||
    fallbackInvoiceNumber ||
    recipientPreview?.invoice_number ||
    "";

  const recipientLabel = isCommission ? "Provider Email" : "Client Email";
  const recipientName =
    recipientPreview?.recipient_contact_name ||
    recipientPreview?.provider_name ||
    fallbackRecipientName ||
    "there";

  const requiresApprove = typeof onApprove === "function";

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setSubmitting(false);
      setError("");
      setRecipientPreview(null);
      setAttachments([]);
      setCaptureDraft(null);
      setPdfPreviewDraft(null);
      setSubject("");
      setRecipientEmail("");
      setCcEmails([]);
      setEmailBody("");
      captureDraftRef.current = null;
      capturePromiseRef.current = null;
      return undefined;
    }

    if (!resolvedInvoiceIds.length) {
      setError("Invoice details are missing.");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.all([
      previewFinanceInvoiceEmailRecipient(resolvedInvoiceIds[0]),
      loadInvoicePreviewDrafts(resolvedInvoiceIds, { clientId, isCommission }),
    ])
      .then(([preview, preparedInvoices]) => {
        if (cancelled) return;

        const nextAttachments = (preparedInvoices || [])
          .map((item) => {
            const invoiceNumber =
              item.invoice_number || item.draft?.invoiceNumber || "";
            return {
              invoiceId: String(item.invoice_id || ""),
              invoiceNumber,
              draft: item.draft || null,
              pdfFilename: buildInvoicePdfFilename(invoiceNumber),
            };
          })
          .filter((item) => item.invoiceId && item.draft);

        if (!nextAttachments.length) {
          throw new Error("Unable to prepare invoice PDF attachments.");
        }

        const numbers = nextAttachments
          .map((item) => item.invoiceNumber)
          .filter(Boolean);

        setRecipientPreview(preview);
        setAttachments(nextAttachments);
        setRecipientEmail(
          String(preview?.recipient_email || fallbackRecipientEmail || "").trim(),
        );
        setSubject(
          buildDefaultEmailSubject({
            invoiceNumbers: numbers.length ? numbers : [fallbackInvoiceNumber].filter(Boolean),
            isCommission,
          }),
        );
        setEmailBody(
          buildDefaultEmailBody({
            recipientName:
              preview?.recipient_contact_name ||
              preview?.provider_name ||
              fallbackRecipientName ||
              "there",
            invoiceNumbers: numbers.length ? numbers : [fallbackInvoiceNumber].filter(Boolean),
            userName: loggedInUser?.name,
            branchLabel,
            branchPhone,
            isCommission,
          }),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Failed to load invoice email details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    resolvedInvoiceIds,
    clientId,
    isCommission,
    fallbackRecipientEmail,
    fallbackRecipientName,
    fallbackInvoiceNumber,
    loggedInUser?.name,
    branchLabel,
    branchPhone,
  ]);

  useEffect(() => {
    if (!captureDraft || !capturePromiseRef.current) {
      return undefined;
    }

    let cancelled = false;

    const runCapture = async () => {
      if (cancelled) return;

      try {
        const draftForPdf = captureDraftRef.current;
        if (!draftForPdf) {
          throw new Error("Invoice draft is not available for PDF export.");
        }
        const pdfBlob = await getPdfBlobFromFinanceInvoiceDraft(draftForPdf);
        if (!cancelled) {
          capturePromiseRef.current?.resolve(pdfBlob);
        }
      } catch (err) {
        if (!cancelled) {
          capturePromiseRef.current?.reject(err);
        }
      } finally {
        if (!cancelled) {
          capturePromiseRef.current = null;
          captureDraftRef.current = null;
          setCaptureDraft(null);
        }
      }
    };

    runCapture();

    return () => {
      cancelled = true;
    };
  }, [captureDraft]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !submitting) {
        if (pdfPreviewDraft) {
          setPdfPreviewDraft(null);
          return;
        }
        handleCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, submitting, pdfPreviewDraft]);

  const canSend =
    !loading &&
    !submitting &&
    isValidEmail(recipientEmail) &&
    ccEmails.every((email) => isValidEmail(email)) &&
    String(subject || "").trim() &&
    !isEmptyEmailHtml(emailBody) &&
    attachments.length > 0;

  const capturePdfForDraft = (draft) =>
    new Promise((resolve, reject) => {
      capturePromiseRef.current = { resolve, reject };
      captureDraftRef.current = draft;
      setCaptureDraft(draft);
    });

  const finalizeApprove = async () => {
    if (!requiresApprove) return;
    await onApprove();
  };

  const handleCancel = async () => {
    if (submitting) return;
    if (!requiresApprove) {
      onClose?.();
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await finalizeApprove();
      onClose?.();
    } catch (err) {
      setError(err?.message || "Failed to approve invoice.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSend || !attachments.length) return;

    setError("");
    setSubmitting(true);

    try {
      const preparedAttachments = [];

      for (const attachment of attachments) {
        const pdfBlob = await capturePdfForDraft(attachment.draft);
        preparedAttachments.push({
          invoiceId: attachment.invoiceId,
          pdfBlob,
          pdfFilename: attachment.pdfFilename,
        });
      }

      await sendFinanceInvoiceApproveEmail({
        clientId: clientId || recipientPreview?.client_id || null,
        recipientName,
        email: recipientEmail.trim(),
        ccEmails,
        subject: subject.trim(),
        emailBody,
        attachments: preparedAttachments,
        approve: requiresApprove,
      });

      const sentCount = attachments.length;
      showCommissionInvoiceSuccessToast(
        sentCount === 1
          ? "Invoice email sent successfully."
          : `Invoice email sent with ${sentCount} PDF attachments.`,
      );
      await finalizeApprove();
      onSent?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || "Failed to send invoice email.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;


  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop bg-black/40"
        onClick={() => !submitting && !pdfPreviewDraft && handleCancel()}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-2 sm:p-3"
        role="dialog"
        aria-modal="true"
        aria-labelledby="finance-invoice-approve-email-title"
        onClick={() => !submitting && !pdfPreviewDraft && handleCancel()}
      >
        <div
          className="tailwind-container relative z-10 flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex items-start justify-between gap-2 border-b border-slate-100 px-4 py-3">
              <div className="min-w-0">
                <h3
                  id="finance-invoice-approve-email-title"
                  className="mb-0 text-[15px] font-semibold text-slate-900"
                >
                  {isCommission ? "Send commission invoice" : "Send invoice"}
                </h3>
                <p className="mb-0 text-[12px] text-slate-500">
                  {loading
                    ? "Loading invoice and recipient details…"
                    : attachments.length > 1
                      ? `${attachments.length} PDF attachments will be sent`
                      : `Invoice ${primaryInvoiceNumber || "—"} · PDF attachment included`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !submitting && onClose?.()}
                disabled={submitting}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                style={{ boxShadow: "none" }}
                aria-label="Close"
              >
                <FaTimes size={12} />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block min-w-0">
                  <span className="mb-1 block text-[12px] font-semibold text-slate-700">
                    Subject
                  </span>
                  <input
                    type="text"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    disabled={submitting || loading}
                    placeholder="Please enter"
                    className={inputClass}
                  />
                </label>

                <label className="block min-w-0">
                  <span className="mb-1 block text-[12px] font-semibold text-slate-700">
                    {recipientLabel}
                  </span>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(event) => setRecipientEmail(event.target.value)}
                    disabled={submitting || loading}
                    placeholder="Please enter"
                    className={inputClass}
                  />
                  <p className="mb-0 mt-1 text-[11px] text-slate-500">
                    {getRecipientHint(recipientPreview, isCommission)}
                  </p>
                </label>
              </div>

              <CcEmailsField
                emails={ccEmails}
                onChange={setCcEmails}
                disabled={submitting || loading}
              />

              <div>
                <span className="mb-1 block text-[12px] font-semibold text-slate-700">
                  Attachments
                </span>
                {loading ? (
                  <p className="mb-0 text-[12px] text-slate-500">
                    Preparing PDF attachments…
                  </p>
                ) : attachments.length ? (
                  <ul className="mb-0 space-y-2">
                    {attachments.map((attachment) => (
                      <li
                        key={attachment.invoiceId}
                        className="flex items-center justify-between gap-2 rounded-md border border-[#eef0ff] bg-[#f8f9ff] px-3 py-2"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <FaFilePdf
                            className="shrink-0 text-red-600"
                            size={14}
                            aria-hidden="true"
                          />
                          <div className="min-w-0">
                            <p className="mb-0 truncate text-[12px] font-semibold text-slate-800">
                              {attachment.pdfFilename}
                            </p>
                            {attachment.invoiceNumber ? (
                              <p className="mb-0 text-[11px] text-slate-500">
                                Invoice {attachment.invoiceNumber}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          disabled={submitting || loading}
                          onClick={() => setPdfPreviewDraft(attachment.draft)}
                        >
                          <FaEye size={10} />
                          Preview
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mb-0 text-[12px] text-slate-500">
                    No PDF attachments available.
                  </p>
                )}
              </div>

              <div>
                <span className="mb-1 block text-[12px] font-semibold text-slate-700">
                  Email message
                </span>
                <EmailRichTextEditor
                  value={emailBody}
                  onChange={setEmailBody}
                  placeholder="Please Enter"
                />
              </div>

              {error ? (
                <p className="mb-0 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[12px] font-medium text-rose-700">
                  {error}
                </p>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={submitting}
                className="inline-flex h-9 items-center rounded-md bg-red-600 px-4 text-[12px] font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                style={{ border: "none", boxShadow: "none" }}
              >
                {submitting && requiresApprove ? "Approving…" : "Cancel"}
              </button>
              <button
                type="submit"
                disabled={!canSend}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-emerald-600 px-4 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ border: "none", boxShadow: "none" }}
              >
                <FaPaperPlane size={11} />
                {submitting
                  ? "Sending…"
                  : attachments.length > 1
                    ? "Send email"
                    : "Send email"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {pdfPreviewDraft ? (
        <>
          <div
            className="modal-backdrop fade show invoice-xero-preview-backdrop invoice-xero-preview-backdrop--stacked"
            onClick={() => !submitting && setPdfPreviewDraft(null)}
            aria-hidden="true"
          />
          <div
            className="invoice-xero-preview-modal invoice-xero-preview-modal--stacked"
            onClick={(event) => event.stopPropagation()}
          >
            <InvoiceXeroPreview
              draft={pdfPreviewDraft}
              onClose={() => setPdfPreviewDraft(null)}
              showToolbar
            />
          </div>
        </>
      ) : null}

      {captureDraft ? (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            zIndex: -1,
            opacity: 0,
            pointerEvents: "none",
            width: "210mm",
            background: "#fff",
          }}
        >
          <InvoiceXeroPreview
            draft={captureDraft}
            embedded
            showToolbar={false}
            captureRef={pdfCaptureRef}
          />
        </div>
      ) : null}
    </>,
    document.body,
  );
}

export default FinanceInvoiceApproveEmailModal;
